/**
 * UpgradePage.js
 * CMS self-service upgrade page — shows Zeus Core / Pro / Elite pricing.
 * Only org owners can trigger upgrades. Non-owners see a contact-owner message.
 * Annual pricing is the default lead; M-t-M shown as secondary.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ConfirmModal from '../components/common/ConfirmModal';
import { Check, Zap, Brain, Globe, Headphones, Users, Star, ArrowRight, Mail } from 'lucide-react';

const FEATURE_LABELS = {
  case_management:    'Case Management',
  task_management:    'Task Management',
  calendar:          'Calendar & Events',
  intake_forms:      'Client Intake Forms',
  messenger:         'Messenger',
  care_providers:    'Provider Network',
  document_templates:'Document Templates',
  billing:           'Billing & Invoicing',
  letters_forms:     'Letters & Forms',
  reports:           'Reports & Analytics',
  workflows:         'Workflows & Automation',
  ocr:               'OCR Document Reader',
  api_access:        'API Access',
};

const PLAN_STYLES = {
  core:  { color: '#3b82f6', bg: 'rgba(59,130,246,0.06)',  border: 'rgba(59,130,246,0.2)'  },
  pro:   { color: '#d4a853', bg: 'rgba(212,168,83,0.07)',  border: 'rgba(212,168,83,0.35)' },
  elite: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.06)', border: 'rgba(139,92,246,0.25)' },
  trial: { color: '#64748b', bg: 'rgba(100,116,139,0.04)', border: 'rgba(100,116,139,0.2)' },
};

const SUPPORT_LABELS = {
  email:     'Email support',
  priority:  'Priority support + onboarding call',
  dedicated: 'Dedicated account manager',
};

export default function UpgradePage() {
  const navigate  = useNavigate();
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(null);
  const [success, setSuccess] = useState(null);
  const [error,   setError]   = useState('');
  const [billing, setBilling] = useState('annual'); // 'annual' | 'mtm'
  const [confirmAction, setConfirmAction] = useState(null);

  const user = JSON.parse(localStorage.getItem('zeus_user') || '{}');
  const isOwner   = ['owner', 'super_admin'].includes(user.role) || ['owner', 'super_admin'].includes(user.access_level);
  const currentPlan = user.subscription_plan || '';

  useEffect(() => {
    api.get('/plans')
      .then(data => {
        // Exclude 'trial' from the display; only show purchasable tiers
        setPlans((data || []).filter(p => p.slug !== 'trial'));
      })
      .catch(() => setError('Failed to load plans. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async (slug) => {
    if (!isOwner) return;
    setConfirmAction({
      title: 'Switch Plan',
      message: `Switch your firm to the ${slug.charAt(0).toUpperCase() + slug.slice(1)} plan?`,
      confirmLabel: 'Switch Plan',
      danger: false,
      onConfirm: async () => {
        setBusy(slug);
        setError('');
        try {
          const res = await api.post('/plans/upgrade', { plan_slug: slug });
          setSuccess(res.name || slug);
          const me = await api.get('/auth/me');
          localStorage.setItem('zeus_user', JSON.stringify({ ...user, ...me }));
          setTimeout(() => navigate('/'), 2000);
        } catch (err) {
          setError(err.message || 'Upgrade failed. Please contact support.');
        } finally { setBusy(null); }
      },
    });
  };

  const isCurrentPlan = (slug) => currentPlan === slug;

  const isDowngrade = (slug) => {
    const ORDER = ['trial', 'core', 'pro', 'elite'];
    return ORDER.indexOf(slug) < ORDER.indexOf(currentPlan);
  };

  if (loading) return (
    <>
      <div style={{ padding: '60px 0', textAlign: 'center', color: '#888' }}>Loading plans…</div>
    </>
  );

  if (success) return (
    <>
      <div style={{ padding: '60px 24px', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Check size={26} style={{ color: '#22c55e' }} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem' }}>Plan updated to {success}</h2>
        <p style={{ color: '#888', marginBottom: 20 }}>Redirecting to dashboard…</p>
      </div>
    </>
  );

  return (
    <>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '32px 20px 60px' }}>

        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px' }}>Plans & Pricing</h1>
          <p style={{ color: '#888', fontSize: '0.95rem', margin: '0 0 20px' }}>
            Built for how attorneys actually work — from intake to resolution, in one place.
          </p>

          {/* Billing toggle */}
          <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 4 }}>
            {[{ val: 'annual', label: 'Annual' }, { val: 'mtm', label: 'Monthly' }].map(opt => (
              <button key={opt.val} onClick={() => setBilling(opt.val)} style={{
                padding: '6px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                background: billing === opt.val ? '#fff' : 'transparent',
                color: billing === opt.val ? '#0f172a' : '#64748b',
                boxShadow: billing === opt.val ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.15s',
              }}>
                {opt.label}
                {opt.val === 'annual' && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#22c55e', fontWeight: 700 }}>Save 20%</span>}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 16px', color: '#ef4444', marginBottom: 20, fontSize: '0.88rem' }}>
            {error}
          </div>
        )}

        {!isOwner && (
          <div style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.3)', borderRadius: 10, padding: '12px 18px', marginBottom: 24, fontSize: '0.88rem', color: '#92691a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={16} />
            Only your firm owner can upgrade the plan. Contact your firm administrator to make changes.
          </div>
        )}

        {/* ── Plan Cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 18, marginBottom: 40 }}>
          {plans.map(plan => {
            const s = PLAN_STYLES[plan.slug] || PLAN_STYLES.core;
            const isCurrent  = isCurrentPlan(plan.slug);
            const isDown     = isDowngrade(plan.slug);
            const isPopular  = plan.highlight;
            const price      = billing === 'annual' ? plan.price_per_seat_annual : plan.price_per_seat_mtm;
            const features   = Object.entries(plan.features || {}).filter(([, v]) => v).map(([k]) => k);

            return (
              <div key={plan.slug} style={{
                background: '#fff',
                borderRadius: 14,
                border: `2px solid ${isCurrent ? s.color : isPopular ? s.border : '#e2e8f0'}`,
                boxShadow: isPopular ? `0 4px 24px ${s.border}` : '0 1px 4px rgba(0,0,0,0.06)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Popular badge */}
                {isPopular && (
                  <div style={{ background: s.color, color: plan.slug === 'pro' ? '#000' : '#fff', padding: '5px 16px', textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <Star size={10} /> {plan.highlight_label || 'Most Popular'}
                  </div>
                )}
                {isCurrent && !isPopular && (
                  <div style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', padding: '5px 16px', textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#16a34a' }}>
                    Your Current Plan
                  </div>
                )}

                <div style={{ padding: '22px 22px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Plan name + price */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: '1.1rem', color: s.color }}>{plan.name}</span>
                      {isCurrent && isPopular && <span style={{ fontSize: '0.65rem', background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 6, padding: '1px 7px', fontWeight: 700 }}>Current Plan</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a' }}>
                        {price === 0 ? 'Free' : `$${price}`}
                      </span>
                      {price > 0 && <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>/user/mo</span>}
                    </div>
                    {billing === 'annual' && price > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                        Billed annually · ${plan.price_per_seat_mtm}/mo billed monthly
                      </div>
                    )}
                    {plan.max_seats_per_seat && (
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>Up to {plan.max_seats_per_seat} users</div>
                    )}
                  </div>

                  {/* Bundle pricing callout */}
                  {(plan.bundle_small_annual || plan.bundle_mid_annual) && (
                    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: '0.78rem' }}>
                      <div style={{ fontWeight: 700, color: s.color, marginBottom: 4 }}>Firm Bundles</div>
                      {plan.bundle_small_annual && <div style={{ color: '#475569' }}>5 users — <strong>${plan.bundle_small_annual}/mo</strong></div>}
                      {plan.bundle_mid_annual   && <div style={{ color: '#475569' }}>10 users — <strong>${plan.bundle_mid_annual}/mo</strong></div>}
                    </div>
                  )}

                  {/* Capability highlights */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
                    {plan.workflow_limit !== 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: s.bg, border: `1px solid ${s.border}`, color: s.color, fontSize: '0.72rem', fontWeight: 600 }}>
                        <Zap size={9} /> {plan.workflow_limit === -1 ? 'Unlimited Workflows' : `${plan.workflow_limit} Workflows`}
                      </span>
                    )}
                    {plan.has_ocr && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)', color: '#8b5cf6', fontSize: '0.72rem', fontWeight: 600 }}>
                        <Brain size={9} /> OCR Document Reader
                      </span>
                    )}
                    {plan.has_api && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.2)', color: '#8b5cf6', fontSize: '0.72rem', fontWeight: 600 }}>
                        <Globe size={9} /> API Access
                      </span>
                    )}
                    {plan.support_level && plan.support_level !== 'email' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', color: '#16a34a', fontSize: '0.72rem', fontWeight: 600 }}>
                        <Headphones size={9} /> {SUPPORT_LABELS[plan.support_level] || plan.support_level}
                      </span>
                    )}
                  </div>

                  {/* Feature list */}
                  <div style={{ flex: 1, marginBottom: 18 }}>
                    {features.map(k => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', fontSize: '0.82rem', color: '#374151' }}>
                        <Check size={13} style={{ color: s.color, flexShrink: 0 }} />
                        {FEATURE_LABELS[k] || k}
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  {isCurrent ? (
                    <div style={{ textAlign: 'center', padding: '10px', borderRadius: 9, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                      Current Plan
                    </div>
                  ) : isDown ? (
                    <div style={{ textAlign: 'center', padding: '10px', borderRadius: 9, background: '#fafafa', border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#94a3b8' }}>
                      To downgrade, contact{' '}
                      <a href="mailto:support@zeuscms.com" style={{ color: '#d4a853', textDecoration: 'none', fontWeight: 600 }}>support</a>
                    </div>
                  ) : isOwner ? (
                    <button
                      onClick={() => handleUpgrade(plan.slug)}
                      disabled={!!busy}
                      style={{
                        width: '100%', padding: '11px', borderRadius: 9, border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                        background: isPopular ? s.color : s.bg,
                        color: isPopular ? (plan.slug === 'pro' ? '#000' : '#fff') : s.color,
                        fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        opacity: busy ? 0.7 : 1, transition: 'opacity 0.15s',
                        border: isPopular ? 'none' : `1px solid ${s.border}`,
                      }}>
                      {busy === plan.slug ? 'Processing…' : <><span>Upgrade to {plan.name}</span><ArrowRight size={14} /></>}
                    </button>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '10px', borderRadius: 9, background: '#fafafa', border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#94a3b8' }}>
                      Contact your firm owner to upgrade
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Bundle Pricing Note ── */}
        <div style={{ background: '#f8fafc', borderRadius: 12, padding: '20px 24px', marginBottom: 32, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
            Firm Bundles — Remove per-seat anxiety
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6 }}>
            Bundles include a fixed number of users at a flat monthly rate — perfect when you're adding paralegals and intake staff.
            Bundling is most attractive at or near the included seat count.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {[
              { label: 'Solo (1–2 users)', note: 'Per-seat pricing' },
              { label: 'Small Firm (up to 5)', note: 'Core $249 · Pro $399 · Elite $649' },
              { label: 'Mid Firm (up to 10)',  note: 'Core $449 · Pro $749 · Elite $1,199' },
              { label: 'Enterprise (10+)',      note: 'Contact us for custom pricing' },
            ].map(r => (
              <div key={r.label} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a', marginBottom: 2 }}>{r.label}</div>
                <div style={{ fontSize: '0.76rem', color: '#64748b' }}>{r.note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Enterprise CTA ── */}
        <div style={{ textAlign: 'center', padding: '28px', background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(212,168,83,0.06))', borderRadius: 14, border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700 }}>10+ users? Let's talk.</h3>
          <p style={{ margin: '0 0 16px', fontSize: '0.88rem', color: '#64748b' }}>
            Enterprise accounts get custom pricing, a dedicated account manager, and white-glove onboarding.
          </p>
          <a href="mailto:sales@zeuscms.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 22px', borderRadius: 9, background: '#0f172a', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '0.88rem' }}>
            <Mail size={14} /> Contact Sales
          </a>
        </div>

      </div>
      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </>
  );
}
